import React, { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { Grid, CircularProgress, Box, makeStyles, Theme, Icon, Button } from '@material-ui/core';
import { capitalizeAll, capitalizeFirst, GenericMessageBox, Time, TimeSeriesType, TimeSeriesValueType } from '@moderepo/mode-app-components';
import { useNavigate } from 'react-router-dom';
import { modeUIContext, showErrorNotification, showSlideOutPanel, SlideOutPanelLocation } from '@moderepo/mode-ui-state';
import { KeyboardDateTimePicker } from '@material-ui/pickers';
import { MaterialUiPickersDate } from '@material-ui/pickers/typings/date';
import produce from 'immer';
import { TimeSeriesAggregation } from '@moderepo/mode-apis';
import { fetchProjectDeviceById, ritzDataContext, selectProjectById, selectProjectDeviceById } from '@moderepo/mode-data-state-ritz';
import { fetchRobotCloudProjectSettings, rcDataContext, selectRobotCloudProjectSettings } from '@moderepo/mode-data-state-robot-cloud';
import { fetchDeviceKVStore, userAppDataContext, selectDeviceKVStore } from '@moderepo/mode-data-state-user-app';
import { useRitzTranslation } from '../../utils/customHooks';
import { RCCollectionLatestMetricDisplayContainer } from './RCCollectionLatestMetricDisplayContainer';
import { RCRouteHelper } from './RCRouteHelper';
import { DownloadTSDataCompOptions, SlideOutPanelCompType } from '../routes/defaultProjectConfig';



const useStyle = makeStyles((theme: Theme) => {
    return {
        root: {
            marginBottom: theme.spacing(15),
        },

        toolBar: {
            padding      : theme.spacing(2, 0),
            display      : 'flex',
            flexDirection: 'row',
            alignItems   : 'center',
            flexWrap     : 'wrap',
        },

        toolBarTitle: {
            fontSize    : 18,
            fontWeight  : 500,
            whiteSpace  : 'nowrap',
            paddingRight: theme.spacing(2),
        },

        timeSeriesFilterMenu: {
            display   : 'flex',
            alignItems: 'center',
        },

        timeSeriesFilterMenuDatesSeparator: {
            margin: theme.spacing(0, 1),
        },

        timeSeriesFilterMenuItem: {
            minWidth: 200,
        },

        timeSeriesFilterMenuItemIcon: {
            minWidth: 'unset',
        },

        filterLabel: {
            margin    : theme.spacing(1),
            fontWeight: 'bold',
            whiteSpace: 'nowrap',
        },
    };
});

// The key of the KV store where gateway collections are stored
const GATEWAY_COLLECTION_INFO_KV_STORE_KEY = 'gateway-collections';


const METRIC_VIEW_MIN_TIME_RANGE = Time.MINUTE_IN_MS;


/**
 * The interface for the dynamic gateway TSDB collection info
 */
export interface GatewayCollectionInfo {
    readonly displayName: string;
    readonly id: string;
}


export interface RCDeviceCollectionsLatestValuesDisplayContainerProps {
    readonly projectId: number;
    readonly deviceId: number;
    readonly userId: number | undefined;
    readonly homeId: number | undefined;

    /**
     * This Container has an action that it needs to be displayed as a page CTA. In other to do this, the parent container must
     * implement the handler function setPageMainAction and take the provided 'action' and display it on the page. The parent container DOES NOT
     * need to do anything with this action other than displaying it. This container will take care of the UI of the action and will handle all the
     * events.
     */
    readonly setPageMainAction: (action: JSX.Element)=> void;
}


/**
 * This containers is used for showing the latest values of ALL collections belonged to a device.
 * First, it will fetch the list of collections from a device DDP with a specific key GATEWAY_COLLECTION_INFO_KV_STORE_KEY. If this DDP exist,
 * this component will use the RCCollectionLatestMetricDisplayContainer to show the latest values for EACH collection in tabular form.
 * @param props
 */
export const RCDeviceCollectionsLatestValuesDisplayContainer: React.FC<RCDeviceCollectionsLatestValuesDisplayContainerProps> = (
    props: RCDeviceCollectionsLatestValuesDisplayContainerProps,
) => {
    const navigate = useNavigate();
    const { dispatch:uiDispatch } = useContext(modeUIContext);
    const { state:dataState, dispatch:dataDispatch } = useContext(ritzDataContext);
    const { state:appDataState, dispatch:appDataDispatch } = useContext(userAppDataContext);
    const { state:rcDataState, dispatch:rcDataDispatch } = useContext(rcDataContext);
    const project = selectProjectById(dataState, props.projectId);
    const device = selectProjectDeviceById(dataState, props.deviceId);
    const { trans } = useRitzTranslation(project);
    const projectSettings = selectRobotCloudProjectSettings(rcDataState, props.projectId);
    const rcRouteHelper = RCRouteHelper.getInstance(navigate);

    // Set the collection's absolute date range by using the project's creationTime as as startDate and NOW as endDate
    // This is just an estimate date bounds because we don't know the actual date bound without fetching the collection info which can be a lot
    // of data to load because a gateway can have many collections
    const collectionDateBounds = useMemo((): {
        readonly minDate: number;
        readonly maxDate: number;
    } | undefined => {
        if (project) {
            return {
                minDate: Date.parse(project.creationTime),
                maxDate: Date.now(),
            };
        }
        return undefined;
    }, [project]);


    // Make the default to be 1 day
    const [selectedDateRange, setSelectedDateRange] = useState<{
        readonly startDate: number;
        readonly endDate: number;
    }>({
        startDate: Date.now() - Time.DAY_IN_MS,
        endDate  : Date.now(),
    });


    // Get the KV store where the collections info are defined
    const gatewayCollectionKVStore = selectDeviceKVStore(appDataState, props.deviceId, GATEWAY_COLLECTION_INFO_KV_STORE_KEY);

    // If the KV exits (not all gateway has this), get the kv store's value and convert it to GatewayCollectionInfo and also sort them by displayName
    const gatewayCollections = useMemo((): readonly GatewayCollectionInfo[] | undefined => {
        if (gatewayCollectionKVStore) {
            return [...gatewayCollectionKVStore.value as GatewayCollectionInfo[]].sort((
                collection1: GatewayCollectionInfo, collection2: GatewayCollectionInfo,
            ) => {
                if (collection1.displayName > collection2.displayName) {
                    return 1;
                }
                if (collection1.displayName < collection2.displayName) {
                    return -1;
                }
                return 0;
            });
        }

        return undefined;
    }, [gatewayCollectionKVStore]);


    const classes = useStyle();


    /**
     * This useEffect is used for fetching projectSettings so we can get the tsdbSmartModule id
     */
    useEffect(() => {
        if (!projectSettings) {
            rcDataDispatch(fetchRobotCloudProjectSettings(props.projectId)).catch(() => {
                // ignore error because dataDispatch already handled the error
            });
        }
    }, [projectSettings, props.projectId, rcDataDispatch]);


    /**
     * This useEffect is used for loading device data on component init
     */
    useEffect(() => {
        dataDispatch(fetchProjectDeviceById(props.projectId, props.deviceId)).catch(() => { /* ignore error */ });
    }, [props.projectId, props.deviceId, dataDispatch]);


    /**
     * Fetch the KV store if we have not fetch it yet
     */
    useEffect(() => {
        if (!gatewayCollectionKVStore) {
            appDataDispatch(fetchDeviceKVStore(props.deviceId, GATEWAY_COLLECTION_INFO_KV_STORE_KEY), true, true).catch(() => { /* ignore error */ });
        }
    }, [appDataDispatch, gatewayCollectionKVStore, props.deviceId]);



    /**
     * On the View Details click from the RCCollectionLatestMetricDisplayContainer component, take the user to the view
     * collection historical data page
     */
    const onViewDetailsClicked = useCallback((collectionId: string) => {
        const collection = gatewayCollections?.find((collectionInfo: GatewayCollectionInfo) => {
            return collectionInfo.id === collectionId;
        });

        if (device && projectSettings) {
            rcRouteHelper.gotoDeviceDataPage({
                projectId        : props.projectId,
                deviceId         : props.deviceId,
                dataId           : collectionId,
                userId           : props.userId,
                homeId           : device.homeId,
                tsdbSmartModuleId: projectSettings.tsdbSmartModuleId,
                timeSeriesType   : TimeSeriesType.COLLECTION,
                valueType        : TimeSeriesValueType.AGGREGATED,
                dataDisplayName  : collection ? collection.displayName : undefined,
            });
        }
    }, [device, gatewayCollections, projectSettings, props.deviceId, props.projectId, props.userId, rcRouteHelper]);


    const onDownloadDataClicked = useCallback((selectedCollectionId?: string | undefined) => {
        if (device && gatewayCollections && collectionDateBounds && projectSettings) {
            const options: DownloadTSDataCompOptions = {
                location : SlideOutPanelLocation.RIGHT,
                compType : SlideOutPanelCompType.DOWNLOAD_TS_DATA_REQUEST,
                compProps: {
                    data: {
                        projectId        : props.projectId,
                        homeId           : device.homeId,
                        tsdbSmartModuleId: projectSettings.tsdbSmartModuleId,
                        dataInfo         : gatewayCollections?.map((collection: GatewayCollectionInfo) => {
                            return {
                                type          : TimeSeriesType.COLLECTION,
                                id            : collection.id,
                                name          : collection.displayName,
                                exportFileName: collection.displayName,
                                selected      : !selectedCollectionId || collection.id === selectedCollectionId,
                            };
                        }),
                        startDate: Math.max(collectionDateBounds.minDate, collectionDateBounds.maxDate - Time.MONTH_IN_MS),
                        endDate  : collectionDateBounds.maxDate,
                        minDate  : collectionDateBounds.minDate,
                        maxDate  : collectionDateBounds.maxDate,
                    },
                },
            };
            uiDispatch(showSlideOutPanel(options));
        }
    }, [collectionDateBounds, device, gatewayCollections, projectSettings, props.projectId, uiDispatch]);


    /**
     * On start date/time selected
     */
    const onStartTimeChange = useCallback((value: MaterialUiPickersDate) => {
        if (value && collectionDateBounds) {
            const timestamp = value.valueOf();
            // make sure the new value is different before updating the state or else the chart will re-render for no reason
            if (timestamp !== selectedDateRange.startDate && timestamp <= selectedDateRange.endDate - METRIC_VIEW_MIN_TIME_RANGE) {
                setSelectedDateRange(produce(selectedDateRange, (draft: any) => {
                    draft.startDate = Math.min(collectionDateBounds.maxDate, Math.max(collectionDateBounds.minDate, timestamp));
                }));
            } else if (timestamp > selectedDateRange.endDate - METRIC_VIEW_MIN_TIME_RANGE) {
                uiDispatch(showErrorNotification('notif.invalid_selected_date_time'));
            }
        }
    }, [collectionDateBounds, selectedDateRange, uiDispatch]);


    /**
     * On end date/time selected
     */
    const onEndTimeChange = useCallback((value: MaterialUiPickersDate) => {
        if (value && collectionDateBounds) {
            const timestamp = value.valueOf();
            // make sure the new value is different before updating the state or else the chart will re-render for no reason
            if (timestamp !== selectedDateRange.endDate && timestamp >= selectedDateRange.startDate + METRIC_VIEW_MIN_TIME_RANGE) {
                setSelectedDateRange(produce(selectedDateRange, (draft: any) => {
                    draft.endDate = Math.min(collectionDateBounds.maxDate, Math.max(collectionDateBounds.minDate, timestamp));
                }));
            } else if (timestamp < selectedDateRange.startDate + METRIC_VIEW_MIN_TIME_RANGE) {
                uiDispatch(showErrorNotification('notif.invalid_selected_date_time'));
            }
        }
    }, [collectionDateBounds, selectedDateRange, uiDispatch]);


    /**
     * Create a Download Button which will be passed to the parent container for display
     */
    const downloadButton = useMemo(() => {
        return (
            <Button
                color="primary"
                variant="contained"
                startIcon={<Icon>get_app</Icon>}
                disabled={!gatewayCollections}
                onClick={() => {
                    onDownloadDataClicked();
                }}
            >{capitalizeAll(trans('label.download_time_series_data'))}
            </Button>
        );
    }, [gatewayCollections, onDownloadDataClicked, trans]);


    /**
     * on component init, call the parent's setExternalComp handler function and pass it the download button to display
     */
    useEffect(() => {
        props.setPageMainAction(downloadButton);
    }, [downloadButton, props, props.setPageMainAction]);


    return (
        <div className={classes.root}>
            {device && device.homeId && projectSettings && gatewayCollections && collectionDateBounds && (
                <div>
                    <div className={classes.toolBar}>
                        <div className={classes.toolBarTitle}>{capitalizeAll(trans('label.select_date_range'))}</div>
                        <div className={classes.timeSeriesFilterMenu}>
                            <KeyboardDateTimePicker
                                label={capitalizeFirst(trans('label.start_date'))}
                                allowKeyboardControl
                                minDate={collectionDateBounds.minDate}
                                maxDate={collectionDateBounds.maxDate}
                                value={selectedDateRange.startDate}
                                format="YYYY/MM/DD HH:mm:ss"
                                ampm={false}
                                todayLabel={capitalizeFirst(trans('label.today'))}
                                okLabel={capitalizeFirst(trans('label.ok'))}
                                cancelLabel={capitalizeFirst(trans('label.cancel'))}
                                showTodayButton
                                disableFuture
                                onChange={onStartTimeChange}
                            />
                            <Icon className={classes.timeSeriesFilterMenuDatesSeparator} fontSize="small">forward</Icon>
                            <KeyboardDateTimePicker
                                label={capitalizeFirst(trans('label.end_date'))}
                                allowKeyboardControl
                                minDate={selectedDateRange.startDate + METRIC_VIEW_MIN_TIME_RANGE}
                                maxDate={collectionDateBounds.maxDate}
                                value={selectedDateRange.endDate}
                                format="YYYY/MM/DD HH:mm:ss"
                                ampm={false}
                                todayLabel={capitalizeFirst(trans('label.today'))}
                                okLabel={capitalizeFirst(trans('label.ok'))}
                                cancelLabel={capitalizeFirst(trans('label.cancel'))}
                                showTodayButton
                                disableFuture
                                onChange={onEndTimeChange}
                            />
                        </div>
                    </div>
                    <Grid container spacing={3}>
                        {gatewayCollections.map((collection: GatewayCollectionInfo) => {
                            return (
                                <Grid item key={collection.id} xs={12} sm={6} md={6} lg={4} xl={3}>
                                    <RCCollectionLatestMetricDisplayContainer
                                        projectId={props.projectId}
                                        homeId={device.homeId}
                                        tsdbSmartModuleId={projectSettings.tsdbSmartModuleId}
                                        collectionName={collection.displayName}
                                        collectionId={collection.id}
                                        aggregationType={TimeSeriesAggregation.SUM}
                                        dateRange={selectedDateRange}
                                        onViewDetailsClicked={onViewDetailsClicked}
                                        onDownloadDataClicked={onDownloadDataClicked}
                                    />
                                </Grid>
                            );
                        })}
                    </Grid>
                </div>
            )}

            {device && (!device.homeId || !gatewayCollections || gatewayCollections.length === 0) && (
                /* Show empty message if there is no time series */
                <Box marginTop={3}>
                    <GenericMessageBox
                        title={trans('generic.no_data.title', {
                            term: capitalizeAll(trans('label.time_series_data', { count: 2 })),
                        })}
                    />
                </Box>
            )}

            {!device && (
                <CircularProgress />
            )}
        </div>
    );
};
